import React, { ReactNode, useCallback, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { theme } from '../../styles/theme';
import { useTabsContext } from './TabsContext';
import { IStyled } from '../../styles/types';

interface IProps extends IStyled {
  children: ReactNode;
  tabIndex: number;
  defaultActive?: boolean;
  disabled?: boolean;
}

const StyledListItem = styled.li<{
  $isActive: boolean;
  $isDisabled?: boolean;
}>`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  height: 32px;
  position: relative;
  padding-right: 12px;
  padding-left: 12px;
  font-size: 12px;
  font-weight: 400;
  line-height: normal;
  color: ${theme.colors.secondary600};
  transition: color 0.2s ease-in-out;
  &:hover {
    cursor: pointer;
    color: ${theme.colors.secondary900};
  }
  ${({ $isActive }) =>
    $isActive &&
    css`
      color: ${theme.colors.secondary900};
      &::after {
        content: '';
        position: absolute;
        bottom: -1px;
        left: 0;
        display: block;
        width: 100%;
        border-bottom: 2px solid ${theme.colors.mainBlue};
      }
    `}
  ${({ $isDisabled }) =>
    $isDisabled &&
    css`
      color: ${theme.colors.secondary400};
      &:hover {
        cursor: default;
        color: ${theme.colors.secondary400};
      }
    `}
`;

const Tab = ({
  children,
  tabIndex,
  defaultActive,
  disabled,
  className,
  ...rest
}: IProps) => {
  const { activeIndex, setActiveIndex } = useTabsContext();

  const handleClick = useCallback(() => {
    if (disabled || activeIndex === tabIndex) {
      return;
    }
    setActiveIndex(tabIndex);
  }, [activeIndex, disabled, setActiveIndex, tabIndex]);

  // In some situations we have to make a specific tab active by default
  useEffect(() => {
    if (defaultActive && activeIndex !== tabIndex) {
      setActiveIndex(tabIndex);
    }
  }, [activeIndex, defaultActive, setActiveIndex, tabIndex]);

  return (
    <StyledListItem
      onClick={handleClick}
      className={className}
      $isActive={tabIndex === activeIndex}
      $isDisabled={disabled}
      {...rest}
    >
      {children}
    </StyledListItem>
  );
};

export default Tab;
